import React, { useMemo, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@material-ui/core';
import { getAreaTypeLabel } from '../../SiteTakeoff/siteTakeoffConstants';
import { enhancedGetPolygonArea } from '../../MapWithDrawing/Measure/mapUtils';
import { splitPolygonByLine } from '../utils/polygonSplit';

const WIDTH = 720;
const HEIGHT = 460;
const PAD = 36;

const getBounds = (paths) => paths.reduce((acc, p) => ({
  minLat: Math.min(acc.minLat, p.lat),
  maxLat: Math.max(acc.maxLat, p.lat),
  minLng: Math.min(acc.minLng, p.lng),
  maxLng: Math.max(acc.maxLng, p.lng),
}), {
  minLat: Infinity,
  maxLat: -Infinity,
  minLng: Infinity,
  maxLng: -Infinity,
});

const buildProjector = (paths) => {
  const bounds = getBounds(paths);
  const lngSpan = bounds.maxLng - bounds.minLng || 0.00001;
  const latSpan = bounds.maxLat - bounds.minLat || 0.00001;
  const scale = Math.min((WIDTH - PAD * 2) / lngSpan, (HEIGHT - PAD * 2) / latSpan);
  const contentWidth = lngSpan * scale;
  const contentHeight = latSpan * scale;
  const offsetX = (WIDTH - contentWidth) / 2;
  const offsetY = (HEIGHT - contentHeight) / 2;

  const toSvg = (p) => ({
    x: offsetX + (p.lng - bounds.minLng) * scale,
    y: offsetY + (bounds.maxLat - p.lat) * scale,
  });
  const fromSvg = ({ x, y }) => ({
    lng: bounds.minLng + (x - offsetX) / scale,
    lat: bounds.maxLat - (y - offsetY) / scale,
  });
  return { toSvg, fromSvg };
};

const pathToPoints = (paths, toSvg) => paths.map(p => {
  const pt = toSvg(p);
  return `${pt.x},${pt.y}`;
}).join(' ');

const SplitAreaModal = ({ open, area, onClose, onConfirm }) => {
  const [splitPoints, setSplitPoints] = useState([]);
  const [error, setError] = useState('');

  const projector = useMemo(() => (area?.paths?.length ? buildProjector(area.paths) : null), [area]);
  const preview = useMemo(() => {
    if (!area?.paths || splitPoints.length !== 2) return null;
    return splitPolygonByLine(area.paths, splitPoints);
  }, [area, splitPoints]);

  if (!area || !projector) return null;

  const svgPolygon = pathToPoints(area.paths, projector.toSvg);
  const splitSvgPoints = splitPoints.map(projector.toSvg);
  const canConfirm = preview?.success;

  const handleSvgClick = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * WIDTH;
    const y = ((event.clientY - rect.top) / rect.height) * HEIGHT;
    setError('');
    setSplitPoints(prev => {
      const next = [...prev, projector.fromSvg({ x, y })];
      return next.length > 2 ? [next[1], next[2]] : next;
    });
  };

  const handleConfirm = () => {
    if (!preview?.success) {
      setError(preview?.error || 'Draw a split line through the area.');
      return;
    }
    const groupId = area.groupId || uuidv4();
    const groupName = area.groupName || `${area.name || getAreaTypeLabel(area.areaType)} split`;
    const replacements = preview.paths.map((paths, index) => {
      const measurements = enhancedGetPolygonArea(paths);
      return {
        name: `${area.name || getAreaTypeLabel(area.areaType)} ${index + 1}`,
        paths,
        area: (measurements.areaFeet || 0).toFixed(2),
        areaAcres: (measurements.areaAcres || 0).toFixed(4),
        perimeter: (measurements.perimeterFeet || 0).toFixed(2),
        groupId,
        groupName,
      };
    });
    onConfirm(replacements);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Split area</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
          Click two points across the selected area to draw the split line. The line must cross the boundary twice.
        </Typography>
        <Box border="1px solid #e0e0e0" borderRadius={8} overflow="hidden" bgcolor="#fafafa">
          <svg
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            width="100%"
            height={HEIGHT}
            onClick={handleSvgClick}
            style={{ display: 'block', cursor: 'crosshair' }}
          >
            <polygon
              points={svgPolygon}
              fill="rgba(25,118,210,0.16)"
              stroke="#1976d2"
              strokeWidth="2"
            />
            {preview?.success && preview.paths.map((paths, idx) => (
              <polygon
                key={idx}
                points={pathToPoints(paths, projector.toSvg)}
                fill={idx === 0 ? 'rgba(76,175,80,0.22)' : 'rgba(255,152,0,0.22)'}
                stroke={idx === 0 ? '#4caf50' : '#ff9800'}
                strokeWidth="2"
              />
            ))}
            {splitSvgPoints.length === 2 && (
              <line
                x1={splitSvgPoints[0].x}
                y1={splitSvgPoints[0].y}
                x2={splitSvgPoints[1].x}
                y2={splitSvgPoints[1].y}
                stroke="#d32f2f"
                strokeWidth="3"
                strokeDasharray="8 5"
              />
            )}
            {splitSvgPoints.map((point, idx) => (
              <circle key={idx} cx={point.x} cy={point.y} r="5" fill="#d32f2f" stroke="#fff" strokeWidth="2" />
            ))}
          </svg>
        </Box>
        {(error || (preview && !preview.success)) && (
          <Typography variant="caption" color="error" style={{ display: 'block', marginTop: 8 }}>
            {error || preview.error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { setSplitPoints([]); setError(''); }}>
          Reset line
        </Button>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          color="primary"
          variant="contained"
          disabled={!canConfirm}
          onClick={handleConfirm}
        >
          Split into two
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SplitAreaModal;
